import { nanoid } from "nanoid";
import type { TDetailedStoryboard, Storyboard } from "pages/media/types";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { Marker, ParsedScript } from "pages/media/helpers/types/";
import { anchorSortComparator } from "pages/media/helpers/scriptAnchors/";
import { sanitizeText } from "utils/text";
dayjs.extend(duration);

export const generateId = (target: "section") => {
  return target + "-" + nanoid();
};

//scripts property only exists in TDetailedStoryboard
//this function only adds `selected` to scripts
export const storyboardToStoryboardDetailed = (
  storyboardData: TDetailedStoryboard | Storyboard | undefined
): TDetailedStoryboard => {
  const newBody = storyboardData?.sections.map((section) => {
    const scripts =
      "scripts" in section
        ? section.scripts?.map((script) => ({
            ...script,
            selected: false,
          }))
        : section.script?.content && section.script.content.length > 0
          ? [
              {
                ...section.script,
                selected: true,
              },
            ]
          : []; // when restoring a SB, copy the selected script in scripts

    return {
      ...section,
      scripts,
    };
  });

  return {
    ...storyboardData,
    title: storyboardData?.title ?? "",
    sections: newBody ?? [],
  };
};

export const storyboardDetailedToStoryboard = (
  storyboard?: TDetailedStoryboard
) => {
  if (!storyboard) return storyboard;
  console.debug("storyboardDetailedToStoryboard", storyboard);
  const result = {
    title: storyboard.title,
    sections: storyboard.sections.map((section) => ({
      id: section.id,
      topic: sanitizeText(section.topic),
      script: section.script
        ? // && section.script.content.length > 0
          { content: section.script.content }
        : null, // backend doesn't like empty
    })),
  };
  return result;
};

export const timePretty = (readingTime: number): string => {
  const readingTimeDuration = dayjs.duration(readingTime);

  const readingTimeParts: string[] = [];
  if (readingTimeDuration.hours() > 0) {
    readingTimeParts.push(readingTimeDuration.format("H[h]"));
  }
  if (readingTimeDuration.minutes() > 0) {
    readingTimeParts.push(readingTimeDuration.format("m[min]"));
  }
  if (readingTimeDuration.seconds() > 0) {
    readingTimeParts.push(readingTimeDuration.format("s[s]"));
  }
  return readingTimeParts.join(" ");
};

export const extractHighlightIds = (text: string): string[] => {
  return Array.from(
    text.matchAll(/\[\[HL-([\w-]+)\]\]/g),
    (match) => match[1] ?? ""
  );
};

const escapeHtml = (s: string): string =>
  s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

/**
 * Render a text range as HTML with non-HL markers embedded as hidden spans.
 * Used for contentEditable innerHTML — NOT a data format.
 *
 * Uses half-open range [rangeStart, rangeEnd) to prevent marker duplication
 * at chunk boundaries.
 */
export function renderRangeAsHtml(
  parsed: ParsedScript,
  rangeStart: number,
  rangeEnd: number
): string {
  const { text, markers } = parsed;

  // Include PRN closings at exactly rangeEnd so they can pair with their
  // opening in this chunk. Otherwise an HL ending on a PRN-tagged word loses
  // the PRN on round-trip — the closing's position equals the HL's closing
  // position, which is the chunk's rangeEnd.
  const rangeMarkers = markers
    .filter(
      (m) =>
        m.tag !== "HL" &&
        m.position >= rangeStart &&
        (m.position < rangeEnd ||
          (m.position === rangeEnd && m.closing && m.tag === "PRN"))
    )
    .sort(anchorSortComparator);

  if (rangeMarkers.length === 0) {
    return escapeHtml(text.slice(rangeStart, rangeEnd));
  }

  // Pair PRN openings with closings. Track matched closings so that two
  // openings sharing the same id (e.g. same pronunciation on adjacent words)
  // each get their own closing instead of both pairing to the first one.
  // The closing must come at or after the opening — a closing that sits at
  // this chunk's rangeStart belongs to the previous chunk and must not pair
  // with an opening further along in this chunk (would render an empty span
  // and rewind, duplicating the text in between).
  const prnPairs = new Map<Marker, Marker | undefined>();
  const prnClosings = new Set<Marker>();
  for (const m of rangeMarkers) {
    if (m.tag === "PRN" && !m.closing) {
      const closing = rangeMarkers.find(
        (c) =>
          c.tag === "PRN" &&
          c.closing &&
          c.id === m.id &&
          c.position >= m.position &&
          !prnClosings.has(c)
      );
      prnPairs.set(m, closing);
      if (closing) prnClosings.add(closing);
    }
  }

  const parts: string[] = [];
  let pos = rangeStart;

  for (const m of rangeMarkers) {
    if (prnClosings.has(m)) continue;
    if (m.position > pos) parts.push(escapeHtml(text.slice(pos, m.position)));

    if (m.tag === "EMO" && !m.closing) {
      parts.push(
        `<span class="emo-tag" data-emo-id="${escapeHtml(m.id)}" contenteditable="false"></span>`
      );
    } else if (m.tag === "SLIDE" && !m.closing) {
      parts.push(
        `<span class="slide-tag" data-slide-id="${escapeHtml(m.id)}" contenteditable="false"></span>`
      );
    } else if (m.tag === "PRN" && !m.closing) {
      const closing = prnPairs.get(m);
      if (closing) {
        const content = text.slice(m.position, closing.position);
        parts.push(
          `<span class="prn-tag" data-pronunciation="${escapeHtml(m.id)}" data-original-word="${escapeHtml(content)}">${escapeHtml(content)}</span>`
        );
        pos = closing.position;
        continue;
      }
    }
    // Never rewind: a zero-width marker (EMO/SLIDE) sorted at the same
    // position as a preceding PRN must not roll pos back to before the
    // PRN's content, which would re-emit that text.
    pos = Math.max(pos, m.position);
  }

  if (pos < rangeEnd) parts.push(escapeHtml(text.slice(pos, rangeEnd)));
  return parts.join("");
}

/**
 * Walk a contentEditable div and extract a ParsedScript.
 *
 * Expected DOM structure:
 * - Text nodes → plain text
 * - span.highlight-tag[id] → HL markers (optional data-incomplete)
 * - span.emo-tag[data-emo-id] → EMO markers
 * - span.prn-tag[data-pronunciation][data-original-word] → PRN markers
 * - span.slide-tag[data-slide-id] → SLIDE markers
 */
export function domToParsedScript(div: HTMLDivElement): ParsedScript {
  const markers: Marker[] = [];
  let text = "";

  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? "";
      return;
    }

    if (!(node instanceof HTMLElement)) {
      for (const child of Array.from(node.childNodes)) walk(child);
      return;
    }

    const el = node;

    if (el.classList.contains("emo-tag")) {
      const id = el.dataset.emoId?.trim();
      if (id)
        markers.push({
          raw: `[[EMO-${id}]]`,
          tag: "EMO",
          id,
          position: text.length,
          closing: false,
        });
      return;
    }

    if (el.classList.contains("slide-tag")) {
      const id = el.dataset.slideId?.trim();
      if (id)
        markers.push({
          raw: `[[SLIDE-${id}]]`,
          tag: "SLIDE",
          id,
          position: text.length,
          closing: false,
        });
      return;
    }

    if (el.classList.contains("prn-tag")) {
      const pron = el.dataset.pronunciation?.trim();
      const original = el.dataset.originalWord;
      const content = el.textContent;
      if (pron && content === original) {
        markers.push({
          raw: `[[PRN-${pron}]]`,
          tag: "PRN",
          id: pron,
          position: text.length,
          closing: false,
        });
        text += content;
        markers.push({
          raw: `[[/PRN-${pron}]]`,
          tag: "PRN",
          id: pron,
          position: text.length,
          closing: true,
        });
      } else {
        text += content;
      }
      return;
    }

    if (el.classList.contains("highlight-tag")) {
      const id = el.id;
      const incomplete = el.dataset.incomplete as "start" | "end" | undefined;
      if (!id) {
        for (const c of Array.from(node.childNodes)) walk(c);
        return;
      }

      if (incomplete !== "end") {
        markers.push({
          raw: `[[HL-${id}]]`,
          tag: "HL",
          id,
          position: text.length,
          closing: false,
        });
      }
      for (const c of Array.from(node.childNodes)) walk(c);
      if (incomplete !== "start") {
        markers.push({
          raw: `[[/HL-${id}]]`,
          tag: "HL",
          id,
          position: text.length,
          closing: true,
        });
      }
      return;
    }

    for (const child of Array.from(node.childNodes)) walk(child);
  };

  for (const child of Array.from(div.childNodes)) walk(child);
  markers.sort(anchorSortComparator);
  return { text, markers };
}

const fixIncompleteHighlights = (
  chunks: Chunk[],
  type: "HL" | "SHL"
): Chunk[] => {
  const openingTagRegex = new RegExp(
    String.raw`^\[\[${type}-([a-z0-9-]+)\]\]$`
  );
  const closingTagRegex = new RegExp(
    String.raw`^\[\[\/${type}-([a-z0-9-]+)\]\]$`
  );

  const result: Chunk[] = [];
  let pendingHighlightId: string | null = null;

  for (const chunk of chunks) {
    if (chunk.isHighlighted) {
      result.push(chunk);
      continue;
    }

    const openMatch = openingTagRegex.exec(chunk.text);
    if (openMatch) {
      pendingHighlightId = openMatch[1] ?? null;
      continue;
    }

    const closeMatch = closingTagRegex.exec(chunk.text);
    if (closeMatch) {
      if (result.length > 0) {
        const prevChunk = result.at(-1);
        if (prevChunk && !prevChunk.isHighlighted) {
          prevChunk.isHighlighted = true;
          prevChunk.id = closeMatch[1] ?? null;
          prevChunk.incomplete = "end";
        }
      }
      pendingHighlightId = null;
      continue;
    }

    if (pendingHighlightId) {
      result.push({
        text: chunk.text,
        isHighlighted: true,
        id: pendingHighlightId,
        incomplete: "start",
      });
      pendingHighlightId = null;
      continue;
    }

    result.push(chunk);
  }

  return result;
};

export interface Chunk {
  text: string;
  isHighlighted: boolean;
  id: string | null;
  rank?: number;
  incomplete?: "start" | "end";
}

export const splitTextWithHighlightTags = (
  text: string,
  type: "HL" | "SHL" = "HL"
): Chunk[] => {
  const regex = new RegExp(
    String.raw`\[\[${type}-([a-z0-9-]+)\]\](.*?)\[\[\/${type}-[a-z0-9-]+\]\]|(\[\[[^\]]*\]\]|[^[\]]+)`,
    "g"
  );

  const chunks: Chunk[] = [];
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match[2] !== undefined) {
      chunks.push({
        text: match[2],
        isHighlighted: true,
        id: match[1] ?? null,
      });
    } else if (match[3]) {
      chunks.push({
        text: match[3],
        isHighlighted: false,
        id: null,
      });
    }
  }

  return fixIncompleteHighlights(chunks, type);
};
